import { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Tooltip,
  Space,
  Switch,
  Row,
  Col,
  InputNumber,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";

export default function TermsContent({
  form,
  onNext,
  onPrevious,
  interbankTemplate,
}) {
  const [allowRollover, setAllowRollover] = useState(false);
  const [allowEarlyTermination, setAllowEarlyTermination] = useState(false);
  const [earlyTerminationSelection, setEarlyTerminationSelection] =
    useState(null);
  const [allowPlacementFee, setAllowPlacementFee] = useState(false);
  const [placementFeeSelection, setPlacementFeeSelection] = useState(null);
  const [backDatedValueDateAllowed, setBackdatedValueDateAllowed] =
    useState(false);
  const [minTenorValue, setMinTenorValue] = useState("");
  const [maxTenorValue, setMaxTenorValue] = useState("");
  const [defaultTenorValue, setDefaultTenorValue] = useState("");
  const [isEarlyTerminationWithPenalty, setIsEarlyTerminationWithPenalty] =
    useState(false);
  const [isPlacementFeeWithPenalty, setIsPlacementFeeWithPenalty] =
    useState(false);
  const [placementType, setPlacementType] = useState(false);

  // Initialize state from form values when component mounts or form changes
  useEffect(() => {
    // Get initial values from form, providing fallback values
    const initialMinTenor = form.getFieldValue("minTenor") || "";
    const initialMaxTenor = form.getFieldValue("maxTenor") || "";
    const initialDefaultTenor = form.getFieldValue("defaultTenor") || "";
    const initialAllowRollover = form.getFieldValue("allowRollover") || false;
    const initialAllowEarlyTermination =
      form.getFieldValue("allowEarlyTermination") || false;
    const initialEarlyTerminationSelection = form.getFieldValue(
      "earlyTerminationWithPenalty"
    );
    const initialAllowPlacementFee =
      form.getFieldValue("allowPlacementFee") || false;
    const initialPlacementFeeSelection = form.getFieldValue(
      "placementFeeWithPenalty"
    );
    const initialBackdatedValueDateAllowed =
      form.getFieldValue("backDatedValueDateAllowed") || false;

    // Set state values
    setMinTenorValue(initialMinTenor);
    setMaxTenorValue(initialMaxTenor);
    setDefaultTenorValue(initialDefaultTenor);
    setAllowRollover(initialAllowRollover);
    setAllowEarlyTermination(initialAllowEarlyTermination);
    setEarlyTerminationSelection(initialEarlyTerminationSelection);
    setAllowPlacementFee(initialAllowPlacementFee);
    setPlacementFeeSelection(initialPlacementFeeSelection);
    setBackdatedValueDateAllowed(initialBackdatedValueDateAllowed);

    // Set penalty states based on selections
    setIsEarlyTerminationWithPenalty(
      initialAllowEarlyTermination && initialEarlyTerminationSelection === true
    );
    setIsPlacementFeeWithPenalty(
      initialAllowPlacementFee && initialPlacementFeeSelection === true
    );
  }, [form]);

  const handleInputChange = (field, value) => {
    form.setFieldValue(field, value);
  };

  const handleNext = async () => {
    try {
      const res = await form.validateFields([
        "minTenor",
        "maxTenor",
        "defaultTenor",
        "allowRollover",
        "autoRollover",
        "backDatedValueDateAllowed",
        "allowEarlyTermination",
        "earlyTerminationWithPenalty",
        "maturityDateAdjustment",
        "allowPlacementFee",
        "placementFeeWithPenalty",
        "redemptionCharge",
        "penaltyCharge",
        "penaltyType",
      ]);
      console.log("next");
      console.log(res);
      onNext();
    } catch (error) {
      console.error("Validation failed", error);
    }
  };

  // Handle rollover toggle
  const handleRolloverChange = (checked) => {
    setAllowRollover(checked);
    handleInputChange("allowRollover", checked);

    // Clear auto rollover selection when rollover is disabled
    if (!checked) {
      handleInputChange("autoRollover", undefined);
    }
  };

  // Handle early termination toggle
  const handleEarlyTerminationChange = (checked) => {
    setAllowEarlyTermination(checked);
    handleInputChange("allowEarlyTermination", checked);

    // Clear dependent fields when early termination is disabled
    if (!checked) {
      setEarlyTerminationSelection(undefined);
      handleInputChange("earlyTerminationWithPenalty", undefined);
      handleInputChange("redemptionCharge", undefined);
      setIsEarlyTerminationWithPenalty(false);
    }
  };

  const handleEarlyTerminationSelectionChange = (value) => {
    setEarlyTerminationSelection(value);
    handleInputChange("earlyTerminationWithPenalty", value);
    setIsEarlyTerminationWithPenalty(value === true);

    // Clear penalty charge when switching to "Without Penalty"
    if (value === false) {
      handleInputChange("redemptionCharge", undefined);
    }
  };

  // Handle placement fee toggle
  const handlePlacementFeeChange = (checked) => {
    setAllowPlacementFee(checked);
    handleInputChange("allowPlacementFee", checked);

    // Clear dependent fields when placement fee is disabled
    if (!checked) {
      setPlacementFeeSelection(undefined);
      handleInputChange("placementFeeWithPenalty", undefined);
      handleInputChange("penaltyCharge", undefined);
      setIsPlacementFeeWithPenalty(false);
    }
  };

  const handlePlacementFeeSelectionChange = (value) => {
    setPlacementFeeSelection(value);
    handleInputChange("placementFeeWithPenalty", value);
    setIsPlacementFeeWithPenalty(value === true);

    // Clear penalty charge when switching to "Without Penalty"
    if (value === false) {
      handleInputChange("penaltyCharge", undefined);
    }
  };

  // Handle backdated value date toggle
  const handleBackdatedValueDateChange = (checked) => {
    setBackdatedValueDateAllowed(checked);
    handleInputChange("backDatedValueDateAllowed", checked);
  };

  const handleMinTenor = (value) => {
    const numValue = value === "" ? "" : Number(value);
    setMinTenorValue(numValue);
    handleInputChange("minTenor", numValue);

    // Update default tenor to match min tenor if it's less
    const currentDefaultTenor = form.getFieldValue("defaultTenor");
    if (currentDefaultTenor === "" || numValue > currentDefaultTenor) {
      form.setFieldsValue({ defaultTenor: numValue });
      setDefaultTenorValue(numValue);
    }

    // Re-validate max tenor
    form.validateFields(["maxTenor"]);
  };

  const handleMaxTenor = (value) => {
    const numValue = value === "" ? "" : Number(value);
    setMaxTenorValue(numValue);
    handleInputChange("maxTenor", numValue);

    // Re-validate default tenor to ensure it's not > max
    form.validateFields(["defaultTenor"]);
  };

  const handleDefaultTenor = (value) => {
    const numValue = value === "" ? "" : Number(value);
    setDefaultTenorValue(numValue);
    handleInputChange("defaultTenor", numValue);
  };

  const validateMaxTenor = (_, value) => {
    if (!value && value !== 0) {
      return Promise.reject(new Error("Please enter maximum tenor"));
    }

    const maxValue = Number(value);
    const minValue = Number(minTenorValue);

    if (isNaN(maxValue)) {
      return Promise.reject(new Error("Please enter a valid number"));
    }

    if (minValue && maxValue <= minValue) {
      return Promise.reject(
        new Error("Maximum tenor must be greater than minimum tenor")
      );
    }

    return Promise.resolve();
  };

  const validateDefaultTenor = (_, value) => {
    if (!value && value !== 0) {
      return Promise.reject(new Error("Enter default Tenor"));
    }

    const defaultValue = Number(value);
    const minValue = Number(minTenorValue);
    const maxValue = Number(maxTenorValue);

    if (isNaN(defaultValue)) {
      return Promise.reject(new Error("Please enter a valid number"));
    }

    if (minValue && defaultValue < minValue) {
      return Promise.reject(
        new Error("Default tenor cannot be less than minimum tenor")
      );
    }

    if (maxValue && defaultValue > maxValue) {
      return Promise.reject(
        new Error("Default tenor cannot exceed maximum tenor")
      );
    }

    return Promise.resolve();
  };

  const chargesOptions = interbankTemplate?.chargeOptions
    ?.filter(
      (option) =>
        option.active === true &&
        option.chargeAppliesTo?.value?.toLowerCase() === "treasury"
    )
    .map((option) => ({
      value: option.id,
      label: option.name,
      chargeTimeType: option?.chargeTimeType?.value,
      penalty: option?.penalty,
    }));

  return (
    <Card
      style={{
        borderRadius: "12px",
        boxShadow:
          "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "32px",
        }}
      >
        <h1
          style={{
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "32px",
            color: "#101828",
            marginBottom: "0px",
          }}
        >
          Terms
        </h1>
        <p
          style={{
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#344054",
            marginTop: "0px",
          }}
        >
          Define Product Term
        </p>
      </div>
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Minimum Tenor
                  <Tooltip title="Minimum tenor information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="minTenor"
              rules={[
                { required: true, message: "Please enter minimum tenor" },
                {
                  pattern: /^\d+$/,
                  message: "Please enter a valid number",
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
              style={{ flex: 1, marginBottom: 24 }}
            >
              <Space.Compact style={{ display: "flex" }}>
                <Input
                  style={{ flex: 1, height: "44px" }}
                  placeholder="1"
                  type="number"
                  value={minTenorValue}
                  onChange={(e) => handleMinTenor(e.target.value)}
                />
                <Input
                  style={{
                    width: "60px",
                    borderLeft: "none",
                    height: "44px",
                    background: "none",
                  }}
                  placeholder="days"
                  disabled
                />
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Maximum Tenor
                  <Tooltip title="Maximum tenor information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="maxTenor"
              rules={[{ validator: validateMaxTenor }]}
              validateTrigger={["onChange", "onBlur"]}
              style={{ flex: 1, marginBottom: 24 }}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  style={{ flex: 1, height: "44px" }}
                  placeholder="365"
                  type="number"
                  value={maxTenorValue}
                  onChange={(e) => handleMaxTenor(e.target.value)}
                />
                <Input
                  style={{
                    width: "60px",
                    borderLeft: "none",
                    height: "44px",
                    background: "none",
                  }}
                  placeholder="days"
                  disabled
                />
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Default Tenor
                  <Tooltip title="Default tenor information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="defaultTenor"
              rules={[{ validator: validateDefaultTenor }]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Space.Compact style={{ width: "100%" }}>
                <InputNumber
                  style={{ flex: 1, height: "44px" }}
                  placeholder="30"
                  value={defaultTenorValue}
                  onChange={(value) => handleDefaultTenor(value)}
                  className="of-bold-disable-input"
                />
                <Input
                  style={{ width: "60px", borderLeft: "none", height: "44px" }}
                  placeholder="days"
                  disabled
                />
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Maturity Date Adjustment
                  <Tooltip title="Maturity date adjustment information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              style={{ marginBottom: 24 }}
              name={"maturityDateAdjustment"}
            >
              <Select
                placeholder="Next Business Day (If weekend/holiday)"
                style={{ width: "100%", height: "44px" }}
                onChange={(value) =>
                  handleInputChange("maturityDateAdjustment", value)
                }
                options={interbankTemplate?.maturityDateAdjustments?.map(
                  (option) => ({
                    value: option.value,
                    label: option.text,
                  })
                )}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Rollover Section */}
        <Row>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <Form.Item style={{ marginBottom: 16 }} name={"allowRollover"}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Switch
                  size="large"
                  checked={allowRollover}
                  onChange={handleRolloverChange}
                />
                <Space>Rollover Option</Space>
              </div>
            </Form.Item>
          </Col>
          {allowRollover && (
            <Col span={6} style={{ padding: "0px 8px" }}>
              <Form.Item
                style={{ marginBottom: 16 }}
                name={"autoRollover"}
                rules={[
                  {
                    required: true,
                    message: "Please select an option for rollover",
                  },
                ]}
              >
                <Select
                  placeholder="Select option"
                  style={{ width: "100%", height: "44px" }}
                  disabled={!allowRollover}
                  value={
                    allowRollover
                      ? form.getFieldValue("autoRollover")
                      : undefined
                  }
                  onChange={(value) => handleInputChange("autoRollover", value)}
                  options={[
                    { value: false, label: "Manual" },
                    { value: true, label: "Auto" },
                  ]}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Backdated Value Date Section */}
        <Row>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <Form.Item
              style={{ marginBottom: 16 }}
              name={"backDatedValueDateAllowed"}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Switch
                  size="large"
                  checked={backDatedValueDateAllowed}
                  onChange={handleBackdatedValueDateChange}
                />
                <Space>Backdated Value Date Allowed</Space>
              </div>
            </Form.Item>
          </Col>
        </Row>

        {/* Early Termination Section */}
        <Row>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <Form.Item
              style={{ marginBottom: 16 }}
              name={"allowEarlyTermination"}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Switch
                  size="large"
                  checked={allowEarlyTermination}
                  onChange={handleEarlyTerminationChange}
                />
                <Space>Early Termination</Space>
              </div>
            </Form.Item>
          </Col>
          {allowEarlyTermination && (
            <Col span={6} style={{ padding: "0px 8px" }}>
              <Form.Item
                style={{ marginBottom: 16 }}
                name={"earlyTerminationWithPenalty"}
                rules={[
                  {
                    required: true,
                    message: "Please select an option for early termination",
                  },
                ]}
              >
                <Select
                  placeholder="Select option"
                  style={{ width: "100%", height: "44px" }}
                  // disabled={!allowEarlyTermination}
                  value={
                    allowEarlyTermination
                      ? earlyTerminationSelection
                      : undefined
                  }
                  onChange={handleEarlyTerminationSelectionChange}
                  options={[
                    { value: true, label: "With Penalty" },
                    { value: false, label: "Without Penalty" },
                  ]}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Early Termination Penalty Charge */}
        {isEarlyTerminationWithPenalty && (
          <Row>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label="Early Termination Penalty Charge"
                name="redemptionCharge"
                style={{ marginBottom: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Please select option for penalty charge",
                  },
                ]}
              >
                <Select
                  placeholder="Select option"
                  style={{ width: "100%", height: "44px" }}
                  options={interbankTemplate?.penaltyChargeOptions
                    ?.filter(
                      (option) =>
                        option?.chargeTimeType?.value?.toLowerCase() ===
                          "redemption" && option.penalty === true
                    )
                    .map((option) => ({
                      value: option.id,
                      label: option.name,
                    }))}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Placement Fee Section */}
        <Row>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <Form.Item style={{ marginBottom: 16 }} name={"allowPlacementFee"}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Switch
                  size="large"
                  checked={allowPlacementFee}
                  onChange={handlePlacementFeeChange}
                />
                <Space>Placement Fee</Space>
              </div>
            </Form.Item>
          </Col>
          {/* {allowPlacementFee && (
            <Col span={6} style={{ padding: "0px 8px" }}>
              <Form.Item
                style={{ marginBottom: 16 }}
                name={"placementFeeWithPenalty"}
                rules={[
                  {
                    required: true,
                    message: "Please select an option for placement fee",
                  },
                ]}
              >
                <Select
                  placeholder="Select option"
                  style={{ width: "100%", height: "44px" }}
                  // disabled={!allowPlacementFee}
                  value={allowPlacementFee ? placementFeeSelection : undefined}
                  onChange={handlePlacementFeeSelectionChange}
                  options={[
                    { value: true, label: "With Penalty" },
                    { value: false, label: "Without Penalty" },
                  ]}
                />
              </Form.Item>
            </Col>
          )} */}
        </Row>

        {/* Placement Fee Penalty Charge */}
        {allowPlacementFee && (
          <Row>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label="Placement Fee Charge"
                name="penaltyCharge"
                style={{ marginBottom: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Please select a penalty charge option",
                  },
                ]}
              >
                <Select
                  placeholder="Select option"
                  style={{ width: "100%", height: "44px" }}
                  options={chargesOptions
                    ?.filter(
                      (option) =>
                        option.chargeTimeType?.toLowerCase() ===
                        "deal activation"
                    )
                    .map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "32px",
            gap: "12px",
          }}
        >
          <Button style={{ height: "44px" }} onClick={onPrevious}>
            Previous
          </Button>
          <Button
            type="primary"
            style={{ backgroundColor: "#9F1AB1", height: "44px" }}
            onClick={handleNext}
          >
            Next - Interest
          </Button>
        </div>
      </Form>
    </Card>
  );
}
